// Origen: safi-reloaded/frontend/src/stores/Theme/ThemeContext.tsx (commit 369a801).
// Cambios: el contenedor usa `contents` en lugar de `min-h-screen`; la elección se recuerda en `localStorage`
// (sin elección previa, sigue la preferencia del sistema) y el almacenamiento puede fallar sin romper la app.
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

export type ThemeOption = 'default' | 'dark' | 'blue' | 'corporate';

const STORAGE_KEY = 'bf.theme';
const STORED_THEMES: ThemeOption[] = ['default', 'dark'];

interface ThemeContextProps {
	theme: ThemeOption;
	setTheme: (theme: ThemeOption) => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

const initialTheme = (): ThemeOption => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY) as ThemeOption | null;
		if (stored && STORED_THEMES.includes(stored)) return stored;
	} catch {
		// almacenamiento bloqueado: se usa la preferencia del sistema
	}
	try {
		return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'default';
	} catch {
		return 'default';
	}
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
	const [theme, setThemeState] = useState<ThemeOption>(initialTheme);

	const setTheme = useCallback((next: ThemeOption) => {
		setThemeState(next);
		try {
			localStorage.setItem(STORAGE_KEY, next);
		} catch {
			// sin almacenamiento la elección dura solo la sesión de la página
		}
	}, []);

	return (
		<ThemeContext.Provider value={{ theme, setTheme }}>
			<div className={`${theme} contents`}>{children}</div>
		</ThemeContext.Provider>
	);
};

export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (!context) throw new Error('useTheme debe usarse dentro de ThemeProvider');
	return context;
};
